import * as React from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, Button, Link, Radio, Text } from '@factorialco/deprecated-design-system'
import { PayrollPolicyPeriodTypesPeriodTypeEnum } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'

import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'

interface ReimburseModalProps {
  onSubmit: (period: PayrollPolicyPeriodTypesPeriodTypeEnum) => void
  onClose: () => void
  currentPeriodOpen: boolean
}

interface CurrentPeriodRadioProps {
  value: PayrollPolicyPeriodTypesPeriodTypeEnum | null
  isPeriodOpen: boolean
  onChange: (value: PayrollPolicyPeriodTypesPeriodTypeEnum) => void
}

const CurrentPeriodRadio = ({ value, isPeriodOpen, onChange }: CurrentPeriodRadioProps) => {
  if (isPeriodOpen) {
    return (
      <Radio
        name={i18n.t('expenses_v2.payroll.reimburse_modal.current_period')}
        value={value ?? ''}
        onChange={() => onChange('current')}
        description={i18n.t('expenses_v2.payroll.reimburse_modal.current_period_info')}
        option={{
          label: i18n.t('expenses_v2.payroll.reimburse_modal.current_period'),
          value: 'current',
        }}
      />
    )
  }

  return (
    <Box
      background="champagne400"
      paddingX="s24"
      paddingY="s16"
      borderRadius={{
        all: 'abs008',
      }}
    >
      <Text.Normal color="grey1000">
        {i18n.t('expenses_v2.payroll.reimburse_modal.current_period_closed')}
      </Text.Normal>
    </Box>
  )
}

const ReimburseModal = ({ onSubmit, onClose, currentPeriodOpen }: ReimburseModalProps) => {
  const { canManagePayrollSettings, compensationsEnabled, canSeeCompensationsSettings } =
    useOutletContext<RouteContextParams>()
  const [period, setPeriod] = React.useState<PayrollPolicyPeriodTypesPeriodTypeEnum | null>(null)

  const handleSubmit = () => {
    if (!period) return
    onClose()
    onSubmit(period)
  }

  return (
    <Modal
      illustration="list-document"
      title={i18n.t('expenses_v2.list.actions.modal.title_reimburse')}
      description={i18n.t('expenses_v2.list.actions.modal.description_reimburse')}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('expenses_v2.list.actions.modal.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!period}>
            {i18n.t('expenses_v2.list.actions.modal.reimburse')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <FormRow>
        <Radio.Wrap>
          <Box flexDirection="column" gap="s12">
            <CurrentPeriodRadio
              isPeriodOpen={!!currentPeriodOpen}
              value={period}
              onChange={setPeriod}
            />
            <Radio
              name={i18n.t('expenses_v2.payroll.reimburse_modal.following_period')}
              value={period ?? ''}
              onChange={() => setPeriod('next')}
              description={i18n.t('expenses_v2.payroll.reimburse_modal.following_period_info')}
              option={{
                label: i18n.t('expenses_v2.payroll.reimburse_modal.following_period'),
                value: 'next',
              }}
            />
          </Box>
        </Radio.Wrap>
      </FormRow>
      {(canManagePayrollSettings || canSeeCompensationsSettings) && (
        <FormRow>
          <Box
            flexDirection="column"
            background="viridian100"
            paddingX="s24"
            paddingY="s16"
            borderRadius={{
              all: 'abs008',
            }}
            gap="s16"
          >
            <Text.Normal color="grey1000" weight="medium">
              {i18n.t('expenses_v2.payroll.reimburse_modal.categories_banner_title')}
            </Text.Normal>
            <Text.Normal color="grey1000">
              {i18n.t('expenses_v2.payroll.reimburse_modal.categories_banner_description')}
            </Text.Normal>
            <Link.Router
              to={
                compensationsEnabled
                  ? '/settings/compensations/expenses-reimbursement'
                  : '/apps/payroll_bundle/settings/expenses-reimbursement'
              }
            >
              {i18n.t('expenses_v2.payroll.reimburse_modal.categories_banner_link')}
            </Link.Router>
          </Box>
        </FormRow>
      )}
    </Modal>
  )
}

export default observer(ReimburseModal)
